import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

import { getAuthToken } from '@/shared/api/auth-storage'

type RealtimeEcho = Echo<'reverb'>

let echoInstance: RealtimeEcho | null = null
let currentToken: string | null = null

function resolveApiRoot(): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL?.trim() || 'http://localhost:8080/api/v1'
  return apiBase.replace(/\/api\/v1\/?$/, '')
}

function resolveAuthEndpoint(): string {
  return (
    import.meta.env.VITE_REVERB_AUTH_ENDPOINT?.trim() || `${resolveApiRoot()}/broadcasting/auth`
  )
}

function isRealtimeEnabled(): boolean {
  return Boolean(import.meta.env.VITE_REVERB_APP_KEY?.trim())
}

function resolveHost(): string {
  return import.meta.env.VITE_REVERB_HOST?.trim() || window.location.hostname
}

function resolvePort(defaultPort: number): number {
  const raw = import.meta.env.VITE_REVERB_PORT?.trim()
  const parsed = raw ? Number(raw) : NaN
  return Number.isFinite(parsed) ? parsed : defaultPort
}

function resolveScheme(): 'http' | 'https' {
  const configured = import.meta.env.VITE_REVERB_SCHEME?.trim()
  if (configured === 'http' || configured === 'https') {
    return configured
  }
  return window.location.protocol === 'https:' ? 'https' : 'http'
}

export function getEcho(tokenInput?: string | null): RealtimeEcho | null {
  if (!isRealtimeEnabled()) {
    return null
  }

  const token = tokenInput ?? getAuthToken()
  if (!token) {
    return null
  }

  if (echoInstance && currentToken === token) {
    return echoInstance
  }

  if (echoInstance) {
    echoInstance.disconnect()
    echoInstance = null
  }

  const scheme = resolveScheme()
  const forceTLS = scheme === 'https'
  const defaultPort = forceTLS ? 443 : 80
  const wsPort = resolvePort(defaultPort)

  ;(window as unknown as { Pusher?: typeof Pusher }).Pusher = Pusher

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: import.meta.env.VITE_REVERB_APP_KEY,
    wsHost: resolveHost(),
    wsPort,
    wssPort: wsPort,
    forceTLS,
    enabledTransports: ['ws', 'wss'],
    authEndpoint: resolveAuthEndpoint(),
    auth: {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  })

  currentToken = token

  return echoInstance
}

export function getSocketId(): string | null {
  return echoInstance?.socketId() || null
}

export function disconnectEcho(): void {
  if (!echoInstance) {
    currentToken = null
    return
  }

  echoInstance.disconnect()
  echoInstance = null
  currentToken = null
}
